/**
 * Рисованная клавиатура и клавиша-значок.
 *
 * Изображения нарисованы здесь, а не взяты снимками: вектор весит килобайты
 * вместо мегабайтов, не размывается на крупном экране, красится темой сам —
 * и не приносит чужой лицензии. Политика безопасности клиента разрешает
 * изображения только со своего источника, так что стороннюю картинку всё
 * равно пришлось бы класть к себе (ТЗ FR-EN-81).
 *
 * Цвета берутся из токенов темы: `currentColor` для контура и переменные для
 * заливки. Поэтому рисунок сам меняется вместе со светлой и тёмной темой, а
 * проверка контраста остаётся той же, что и для остального интерфейса.
 */

/** Ряд клавиш: ширина клавиши и отступ между ними. */
const KEY = 26;
const GAP = 4;

/** Ряды рисунка: смещение слева и число клавиш. */
const ROWS: readonly { readonly offset: number; readonly keys: number }[] = [
  { offset: 0, keys: 11 },
  { offset: 13, keys: 10 },
  { offset: 26, keys: 9 },
];

/** Клавиши домашнего ряда, выделенные акцентом: с них начинается обучение. */
const HOME_ROW = 1;
const HOME_KEYS = [3, 4, 5, 6];

/** Сколько клавиш на рисунке всего: по ним и считается доля. */
const DRAWN_KEYS = ROWS.reduce((total, row) => total + row.keys, 0);

export interface KeyboardArtProps {
  /** Подпись для чтения с экрана. Рисунок декоративен, если её нет. */
  readonly label?: string;
  /**
   * Освоение клавиш: сколько из скольких.
   *
   * Рисунок показывает долю, а не конкретные клавиши: его тридцать
   * прямоугольников не совпадают с буквами раскладки, и утверждать «вот эта
   * освоена» он не вправе. Точное число говорит подпись рядом с ним
   * (ТЗ UI-67).
   */
  readonly mastered?: number | undefined;
  readonly total?: number | undefined;
}

/**
 * Клавиатура в три ряда: домашний ряд или доля освоенного.
 *
 * Без подписи помечается `aria-hidden`: украшение, о котором диктор объявляет
 * «изображение», — это шум, а не сведения.
 */
export function KeyboardArt({ label, mastered, total }: KeyboardArtProps): React.JSX.Element {
  const width = 11 * KEY + 10 * GAP;
  const height = ROWS.length * KEY + (ROWS.length - 1) * GAP;

  // Без данных рисунок остаётся прежним: подсвечен домашний ряд, с которого
  // начинается обучение.
  const filled =
    mastered === undefined || total === undefined || total === 0
      ? null
      : Math.round((Math.min(mastered, total) / total) * DRAWN_KEYS);

  let drawn = 0;

  return (
    <svg
      className="t2x-art t2x-art--keyboard"
      viewBox={`0 0 ${width} ${height}`}
      role={label === undefined ? 'presentation' : 'img'}
      aria-hidden={label === undefined ? true : undefined}
      aria-label={label}
    >
      {ROWS.map((row, rowIndex) =>
        Array.from({ length: row.keys }, (_, keyIndex) => {
          const home = rowIndex === HOME_ROW && HOME_KEYS.includes(keyIndex);
          const marked = filled === null ? home : drawn++ < filled;

          return (
            <rect
              key={`${rowIndex}:${keyIndex}`}
              x={row.offset + keyIndex * (KEY + GAP)}
              y={rowIndex * (KEY + GAP)}
              width={KEY}
              height={KEY}
              rx={5}
              className={marked ? 't2x-art__key t2x-art__key--home' : 't2x-art__key'}
            />
          );
        }),
      )}
    </svg>
  );
}

export interface KeyCapProps {
  /** Знак на клавише: одна буква или символ. */
  readonly glyph: string;
}

/**
 * Клавиша со знаком: значок раздела.
 *
 * Всегда декоративна — рядом стоит заголовок раздела, и повторять его голосом
 * незачем.
 */
export function KeyCap({ glyph }: KeyCapProps): React.JSX.Element {
  return (
    <svg className="t2x-art t2x-art--cap" viewBox="0 0 32 32" role="presentation" aria-hidden>
      <rect x="1" y="1" width="30" height="30" rx="7" className="t2x-art__key" />
      <text x="16" y="22" textAnchor="middle" className="t2x-art__glyph">
        {glyph}
      </text>
    </svg>
  );
}
